import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Eye, MessageCircle, Share2, MousePointerClick, TrendingUp, Tag, Loader2, BarChart3, Lock } from "lucide-react";

const RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export default function Analytics() {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/overview?days=${range}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setLocked(true); })
      .finally(() => setLoading(false));
  }, [range]);

  if (!loading && locked) return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-8">
        <span className="overline text-primary">Analytics</span>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Offer performance</h1>
        <div className="mt-8 bg-card border border-white/10 rounded-xl p-10 text-center" data-testid="analytics-locked">
          <Lock className="w-10 h-10 text-primary mx-auto mb-4" />
          <h3 className="font-display text-xl font-semibold">Analytics isn't included in your plan</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Upgrade to a plan with the analytics module to see click tracking, top offers, and performance over time.</p>
          <Link to="/dashboard/billing"><Button className="mt-5 rounded-full bg-primary hover:bg-secondary" data-testid="analytics-upgrade-btn">View plans</Button></Link>
        </div>
      </main>
    </div>
  );

  const cards = data ? [
    { label: "Total clicks (all time)", value: data.total_clicks, icon: MousePointerClick },
    { label: `Clicks (last ${range}d)`, value: data.clicks_in_range, icon: TrendingUp },
    { label: `Page views (last ${range}d)`, value: data.views_in_range ?? 0, icon: Eye },
    { label: `WhatsApp taps (last ${range}d)`, value: data.whatsapp_in_range ?? 0, icon: MessageCircle },
    { label: `Shares (last ${range}d)`, value: data.shares_in_range ?? 0, icon: Share2 },
  ] : [];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline text-primary">Analytics</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Offer performance</h1>
          </div>
          <div className="flex gap-1 bg-card border border-white/10 rounded-full p-1" data-testid="range-switch">
            {RANGES.map((r) => (
              <button key={r.days} onClick={() => setRange(r.days)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${range === r.days ? "bg-primary text-white" : "text-muted-foreground hover:text-white"}`} data-testid={`range-${r.label}`}>{r.label}</button>
            ))}
          </div>
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              {cards.map((c) => (
                <div key={c.label} className="bg-card border border-white/10 rounded-xl p-5" data-testid={`analytics-stat-${c.label}`}>
                  <c.icon className="w-5 h-5 text-primary mb-3" />
                  <div className="font-display text-3xl font-bold">{c.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-card border border-white/10 rounded-xl p-6 mt-6" data-testid="clicks-chart">
              <h3 className="font-display font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Views & clicks over time</h3>
              <div className="h-72 mt-4">
                {data.clicks_in_range === 0 && !data.views_in_range ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No clicks recorded in this range yet. Share your page to start tracking.</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.series} margin={{ left: -20, right: 10, top: 10 }}>
                      <defs>
                        <linearGradient id="clk" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#e63946" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#e63946" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2b1414" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#9a8a8a", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" minTickGap={30} />
                      <YAxis tick={{ fill: "#9a8a8a", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#120909", border: "1px solid #2b1414", borderRadius: 8, color: "#fff" }} />
                      <Area type="monotone" dataKey="views" name="Views" stroke="#3b82f6" strokeWidth={2} fill="none" />
                      <Area type="monotone" dataKey="clicks" name="Offer clicks" stroke="#e63946" strokeWidth={2} fill="url(#clk)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-card border border-white/10 rounded-xl p-6 mt-6" data-testid="top-offers-chart">
              <h3 className="font-display font-semibold">Top offers by clicks</h3>
              {data.top_offers.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-4">No offer clicks yet.</p>
              ) : (
                <div className="h-64 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.top_offers} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <XAxis type="number" tick={{ fill: "#9a8a8a", fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="title" tick={{ fill: "#cfc3c3", fontSize: 12 }} width={140} />
                      <Tooltip contentStyle={{ background: "#120909", border: "1px solid #2b1414", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#2b1414" }} />
                      <Bar dataKey="clicks" fill="#800000" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
